import { useCallback } from 'react'
import type { JSX } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Button, SuccessDialog } from '@buffer-mono/popcorn'

import { useChannels } from '~publish/components/PublishSidebar/useChannels'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { channelSettings } from '~publish/legacy/routes'
import { LINKEDIN_CAPABILITIES_SCROLL_HASH } from '~publish/legacy/general-settings/components/LinkedInVerifiedSkills/config'

import { getLinkedInCapabilitiesLaunchChannelId } from './utils'

// Same banner key as app-shell's `useDismissBanner` so prior dismissals carry
// over — users who dismissed this in app-shell won't see it again here.
const LINKEDIN_CAPABILITIES_LAUNCH_BANNER_KEY =
  'linkedin-capabilities-launch-dialog'

const LINKEDIN_CAPABILITIES_IMAGE_URL =
  'https://buffer.com/img/linkedin-capabilities/hero.webp'

/**
 * Inner content + trigger logic for the LinkedIn Capabilities launch dialog.
 * Shows once (per account) when a LinkedIn channel is the active schedule
 * channel and the banner hasn't been dismissed. The target channel is derived
 * reactively from the router, so it also appears when navigating between channel
 * schedules — not just on a fresh load.
 */
export function LinkedInCapabilitiesLaunchDialogContent(): JSX.Element | null {
  const { id: channelId } = useParams<{ id: string }>()
  const { pathname } = useLocation()
  const { channels } = useChannels()
  const navigate = useNavigate()
  const { isActive, dismiss } = useDismissableBanner(
    LINKEDIN_CAPABILITIES_LAUNCH_BANNER_KEY,
  )

  const launchChannelId = getLinkedInCapabilitiesLaunchChannelId({
    channelId,
    pathname,
    channels,
  })

  const handleViewCapabilities = useCallback((): void => {
    if (!launchChannelId) {
      return
    }
    dismiss()
    navigate(
      `${channelSettings.getRoute({
        channelId: launchChannelId,
        tab: 'general',
      })}#${LINKEDIN_CAPABILITIES_SCROLL_HASH}`,
    )
  }, [launchChannelId, dismiss, navigate])

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (!open) {
        dismiss()
      }
    },
    [dismiss],
  )

  if (!isActive || !launchChannelId) {
    return null
  }

  return (
    <SuccessDialog open onOpenChange={handleOpenChange}>
      <SuccessDialog.Content size="medium">
        <SuccessDialog.Image
          src={LINKEDIN_CAPABILITIES_IMAGE_URL}
          alt="Buffer capabilities shown on a LinkedIn profile"
          width={1200}
          height={1062}
        />
        <SuccessDialog.Title>
          Your Buffer capabilities, on your LinkedIn profile
        </SuccessDialog.Title>
        <SuccessDialog.Description>
          When you connect Buffer to a LinkedIn profile, your real Buffer
          activity shows up as capabilities on your profile.
        </SuccessDialog.Description>
        <SuccessDialog.Actions>
          <Button
            size="large"
            variant="primary"
            onClick={handleViewCapabilities}
          >
            View Capabilities
          </Button>
        </SuccessDialog.Actions>
      </SuccessDialog.Content>
    </SuccessDialog>
  )
}
